import axios from 'axios'
import { getImageFromCache, saveImageToCache, initImageCache } from './imageCache'

// create an axios instance
const service = axios.create({
    baseURL: process.env.VUE_APP_BASE_API, // 使用环境变量
    timeout: 5000 // request timeout
})

// request interceptor
service.interceptors.request.use(
    config => {
        // do something before request is sent

        // if (store.getters.token) {
        //     config.headers['X-Token'] = getToken()
        // }
        return config
    },
    error => {
        // do something with request error
        console.log(error) // for debug
        return Promise.reject(error)
    }
)

// response interceptor - 转换本地图片路径为完整 URL
service.interceptors.response.use(
    /**
     * If you want to get http information such as headers or status
     * Please return  response => response
     */

    /**
     * Determine the request status by custom code
     * Here is just an example
     * You can also judge the status by HTTP Status Code
     */
    response => {
        const res = response.data
        // store.commit('SET_LOADING',false);

        // if the custom code is not 20000, it is judged as an error.
        if (res.code !== 200) {
            return Promise.reject(new Error(res.message || 'Error'))
        } else {
            // 转换响应数据中的本地图片路径为完整 URL
            if (res.data) {
                convertImageUrls(res.data)
            }
            return res
        }
    },
    error => {
        console.log('err' + error) // for debug
        return Promise.reject(error)
    }
)

/**
 * 递归转换对象中的本地图片路径为完整 URL
 * @param {Object|Array} data - 需要转换的数据
 */
function convertImageUrls(data) {
    if (!data) return
    
    // 如果是数组，遍历处理
    if (Array.isArray(data)) {
        data.forEach(item => convertImageUrls(item))
        return
    }
    
    // 如果是对象，处理各个图片字段
    if (typeof data === 'object') {
        // 扩展图片字段列表，包含所有可能的图片字段
        const imageFields = [
            'avatar', 'background', 'vxImg', 'aliPlayImg', 'banner', 
            'img', 'typeImgUrl', 'fileUrl', 'imageUrl', 'image'
        ]
        
        imageFields.forEach(field => {
            if (data[field] && typeof data[field] === 'string') {
                data[field] = convertImageUrl(data[field])
            }
        })
        
        // 递归处理嵌套对象
        Object.values(data).forEach(value => {
            if (typeof value === 'object' && value !== null) {
                convertImageUrls(value)
            }
        })
    }
}

/**
 * 转换单个图片 URL
 * @param {string} path - 图片路径
 * @returns {string} - 完整的图片 URL
 */
function convertImageUrl(path) {
    if (!path || typeof path !== 'string') return path
    
    // 如果已经是完整 http/https 路径，直接返回
    if (path.startsWith('http://') || path.startsWith('https://')) {
        return path
    }
    
    // 本地路径，拼接服务器地址
    const baseUrl = process.env.VUE_APP_BASE_API
    const cleanPath = path.replace(/^\/+/, '') // 去除开头的斜杠
    
    // 确保路径包含 /files/ 前缀
    if (!cleanPath.startsWith('files/')) {
        return baseUrl + '/files/' + cleanPath
    }
    
    return baseUrl + '/' + cleanPath
}

/**
 * 从缓存或服务器获取图片（Base64）
 * @param {string} imageUrl - 图片 URL（可能是原始路径或完整 URL）
 * @returns {Promise<string>} - 返回 Base64 格式的图片数据，如果失败则返回空字符串
 */
export async function getCachedImage(imageUrl) {
    if (!imageUrl) {
        return ''
    }

    // 1. 统一转换为完整 URL，确保缓存 key 一致
    const normalizedUrl = convertImageUrl(imageUrl)

    // 2. 确保 IndexedDB 已初始化完成
    try {
        await initImageCache()
    } catch (err) {
        console.warn('IndexedDB 初始化失败，跳过缓存:', err)
    }

    // 3. 先尝试从缓存获取
    try {
        const cachedData = await getImageFromCache(normalizedUrl)
        if (cachedData && cachedData !== '') {
            // 缓存命中，直接返回，不再请求服务器
            return cachedData
        }
    } catch (err) {
        console.warn('读取缓存失败:', err)
    }

    // 4. 缓存未命中，从服务器加载
    try {
        const response = await axios.get(normalizedUrl, {
            responseType: 'blob',
            timeout: 10000
        })

        // 5. 转换为 Base64
        const base64 = await blobToBase64(response.data)

        // 6. 保存到缓存（使用统一后的 URL 作为 key）
        try {
            await saveImageToCache(normalizedUrl, base64)
        } catch (err) {
            console.warn('保存缓存失败:', err)
        }

        return base64
    } catch (error) {
        // 如果加载失败，返回空字符串
        console.error('加载图片失败:', normalizedUrl, error)
        return ''
    }
}

/**
 * Blob 转 Base64
 */
function blobToBase64(blob) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onloadend = () => resolve(reader.result)
        reader.onerror = reject
        reader.readAsDataURL(blob)
    })
}

export default service
